import {
  AUTH_JWT_TOKEN,
  AUTH_JWT_TOKEN_EXPIRATION,
  AUTH_REFRESH_TOKEN,
  AUTH_REFRESH_TOKEN_EXPIRATION,
  Box,
  Button,
  CircularProgress,
  Dialog,
  formatSeconds,
  Stack,
  Typography,
} from "@compass/core";
import { AppResource, AuthResult } from "generated/openApi";
import { ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  setAppAuthStatus,
  setAppReactMenus,
  setAppRoleId,
  setAppRolePermissions,
  setAppTenantName,
  setAppTenantTimezone,
  setAppUserFullName,
  setAppUserMembership,
  setAppUserId,
  setAppCentralUserMembership,
} from "redux/reducers/appSlice";
import { RootState } from "redux/store";
//import {} from "components";
import moment from "moment-timezone";
import { setUserSettingInitialState } from "redux/reducers/userSettingSlice";
import ApiService from "services/apiService";

// ----------------------------------------------------------------------

type AuthGuardProps = {
  children: ReactNode;
};

export const AuthGuard = ({ children }: AuthGuardProps) => {
  const appAuthStatus = useSelector((state: RootState) => state.app.authStatus);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // this flag is used to indicate that if user was authenticated but timed out, so we can allow the user to login in place where they were
  // to continue where they left off (instead of getting redirected to the LoginPage directly)
  const [jwtTokenExpirationCountDown, setJwtTokenExpirationCountDown] = useState(0);
  const [refreshTokenExpirationCountDown, setRefreshTokenExpirationCountDown] = useState(0);
  //const [tokensIntervalId, setTokensIntervalId] = useState<any>();
  const [openSessionTimeoutDialog, setOpenSessionTimeoutDialog] = useState<boolean>(false);
  const [tick, setTick] = useState<number>(0);

  useEffect(() => {
    // check if we are authenticated it (and also get userInfo): this should only happen once (unless browser is refreshed)
    ApiService.home.getAppResource().then((response) => {
      const appResource: AppResource = response;

      moment.tz.setDefault(appResource.timezone);

      dispatch(setAppTenantName(appResource.tenantName));
      dispatch(setAppUserId(appResource.userId));
      dispatch(setAppRoleId(appResource.roleId));
      dispatch(setAppUserMembership(appResource.userMembership));
      dispatch(setAppCentralUserMembership(appResource.centralUserMembership));
      dispatch(setAppUserFullName(appResource.userFullName));
      dispatch(setAppRolePermissions(appResource.rolePermissions));
      dispatch(setAppReactMenus(appResource.reactMenus));
      dispatch(setAppTenantTimezone(appResource.timezone));

      if (appResource.userSetting) dispatch(setUserSettingInitialState(appResource.userSetting));

      dispatch(setAppAuthStatus("AUTHENTICATED"));
    });
  }, []);

  useEffect(() => {
    let tokensIntervalId: any = startTokenTimer();

    return () => {
      clearInterval(tokensIntervalId);
    };
  }, [appAuthStatus]);

  const startTokenTimer = (): NodeJS.Timer => {
    function setAuthTokens() {
      const utcNow = new Date(new Date().toUTCString());

      // on jwtToken: no use for this, for testing purpose only
      const jwtTokenExpirationString: string | null = localStorage.getItem(AUTH_JWT_TOKEN_EXPIRATION);
      if (jwtTokenExpirationString) {
        const jwtTokenExpirationDate = new Date(jwtTokenExpirationString);
        const jwtTokenCountDown = Math.trunc((jwtTokenExpirationDate.valueOf() - utcNow.valueOf()) / 1000);
        setJwtTokenExpirationCountDown(jwtTokenCountDown);
      }

      // on refreshToken: this is what decides session timeleft
      const refreshTokenExpirationString: string | null = localStorage.getItem(AUTH_REFRESH_TOKEN_EXPIRATION);
      if (refreshTokenExpirationString) {
        const refreshTokenExpirationDate = new Date(refreshTokenExpirationString);
        const refreshTokenCountDown = Math.trunc((refreshTokenExpirationDate.valueOf() - utcNow.valueOf()) / 1000);
        setRefreshTokenExpirationCountDown(refreshTokenCountDown);

        // if this many seconds left: show extend session pop up
        if (
          appAuthStatus === "AUTHENTICATED" &&
          refreshTokenCountDown >= 1 &&
          refreshTokenCountDown < Number(process.env.REACT_APP_SESSION_TIMEOUT_SECONDS)
        ) {
          setOpenSessionTimeoutDialog(true);
        }

        // if countdown reaches 0, timeout, clear interval
        if (refreshTokenCountDown <= 0) {
          setOpenSessionTimeoutDialog(false);

          if (appAuthStatus === "AUTHENTICATED") dispatch(setAppAuthStatus("NOT_AUTHENTICATED")); // tell redux we are timedout
        }
      }
    }

    const _tokensIntervalId = setInterval(() => {
      setTick((tick) => tick + 1);
      setAuthTokens();
    }, 1000); // interval occurs every second

    //setTokensIntervalId(_tokensIntervalId);

    return _tokensIntervalId;
  };

  const extendSession = () => {
    const refreshToken: string | null = localStorage.getItem(AUTH_REFRESH_TOKEN);

    if (refreshToken) {
      ApiService.auth.refreshToken(refreshToken).then((response) => {
        const authResult: AuthResult = response;

        localStorage.setItem(AUTH_JWT_TOKEN, authResult.jwtToken!);
        localStorage.setItem(AUTH_JWT_TOKEN_EXPIRATION, authResult.jwtTokenExpiration?.toString()!);
        localStorage.setItem(AUTH_REFRESH_TOKEN, authResult.refreshToken!);
        localStorage.setItem(AUTH_REFRESH_TOKEN_EXPIRATION, authResult.refreshTokenExpiration?.toString()!);

        dispatch(setAppAuthStatus("AUTHENTICATED")); // tell redux we are authenticated
        setOpenSessionTimeoutDialog(false);
      });
    }
  };

  const logout = () => {
    const refreshToken: string | null = localStorage.getItem(AUTH_REFRESH_TOKEN);
    localStorage.removeItem(AUTH_JWT_TOKEN);
    localStorage.removeItem(AUTH_REFRESH_TOKEN);

    if (refreshToken) {
      ApiService.auth.logout(refreshToken);
    }

    dispatch(setAppAuthStatus("NOT_AUTHENTICATED")); // tell redux we are authenticated
  };

  const renderSessionTimeout = () => {
    return (
      <Dialog
        open={true}
        header={<>Session is timing out</>}
        content={
          <>
            You have <b>{formatSeconds(refreshTokenExpirationCountDown)}</b> left in your session before you will be
            automatically logged out of the application. Would you like to extend your session?
          </>
        }
        footer={
          <>
            <Button color={"error"} onClick={() => logout()}>
              Logout
            </Button>
            <Button variant="contained" onClick={() => extendSession()} autoFocus>
              Extend Session
            </Button>
          </>
        }
      />
    );
  };

  const renderAuthenticating = () => {
    return (
      <Stack alignItems="center" justifyContent="center" height="100vh" spacing="lg">
        <CircularProgress size={80} />
        <Typography variant="h3">Authenticating</Typography>
      </Stack>
    );
  };

  if (appAuthStatus === undefined) return renderAuthenticating();

  if (appAuthStatus === "NOT_AUTHENTICATED") {
    return <Navigate to={`/login`} />;
    // return <Navigate to={`/login?redirectTo=${encodeURIComponent(pathname)}`} />;
  }

  if (appAuthStatus === "AUTHENTICATED") {
    return (
      <>
        {/* <div>jwtTokenExpirationCountDown: {jwtTokenExpirationCountDown}</div>
        <div>refreshTokenExpirationCountDown: {refreshTokenExpirationCountDown}</div>
        <div>authStatus: {authStatus}</div>
        <div>tick: {tick}</div>
        <div>{new Date().toString()}</div>
        <div>{new Date().toISOString()}</div> */}

        {openSessionTimeoutDialog && renderSessionTimeout()}

        {/* original content */}
        <Box sx={{ filter: openSessionTimeoutDialog ? "blur(8px)" : "none" }}>{children}</Box>
      </>
    );
  }

  return <>AUTH GUARD AUTHSTATUS NOT SET</>;
};
